import { z } from 'zod';

/**
 * Customer Pre-Registration Schema
 *
 * 顧客事前登録のZodバリデーションスキーマ
 * Laravel: app/Http/Requests/V3/StoreCustomerPreRegistrationRequest.php
 *
 * @version 3.0.0
 */

/**
 * 公開フォーム用スキーマ
 *
 * 一般ユーザーが事前登録フォームから送信する際のバリデーション
 */
export const customerPreRegistrationPublicSchema = z.object({
  // 基本情報
  store_id: z.number({
    message: '店舗IDは数値である必要があります',
  }),

  // アバター画像（オプショナル）
  avatar: z
    .instanceof(File)
    .optional()
    .refine(
      (file) => !file || file.size <= 2048000,
      'アバターのサイズは2MB以下である必要があります'
    )
    .refine(
      (file) =>
        !file || ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type),
      'アバターは画像ファイルである必要があります'
    ),

  // 氏名（漢字）
  family_name: z
    .string({ message: '姓を入力してください' })
    .min(1, '姓を入力してください')
    .max(50, '姓は50文字以内で入力してください'),

  given_name: z
    .string({ message: '名を入力してください' })
    .min(1, '名を入力してください')
    .max(50, '名は50文字以内で入力してください'),

  // 氏名（カナ）
  family_name_kana: z
    .string({ message: '姓（カナ）を入力してください' })
    .min(1, '姓（カナ）を入力してください')
    .max(50, '姓（カナ）は50文字以内で入力してください')
    .regex(/^[ァ-ヶーｧ-ﾝﾞﾟ･・\s]+$/, '姓（カナ）は全角カタカナで入力してください'),

  given_name_kana: z
    .string({ message: '名（カナ）を入力してください' })
    .min(1, '名（カナ）を入力してください')
    .max(50, '名（カナ）は50文字以内で入力してください')
    .regex(/^[ァ-ヶーｧ-ﾝﾞﾟ･・\s]+$/, '名（カナ）は全角カタカナで入力してください'),

  // 生年月日
  birth_date: z
    .union([
      z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '生年月日は正しい形式で入力してください'),
      z.date(),
    ])
    .refine(
      (date) => {
        const dateObj = date instanceof Date ? date : new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateObj < today;
      },
      '生年月日は今日より前の日付を指定してください'
    ),

  // 性別
  gender: z.enum(['male', 'female', 'other'], {
    message: '性別は male, female, other のいずれかを選択してください',
  }),

  // 連絡先
  phone: z
    .string()
    .max(20, '電話番号は20文字以内で入力してください')
    .regex(/^[0-9-]*$/, '電話番号は数字とハイフンのみで入力してください')
    .optional()
    .or(z.literal('')),

  mobile: z
    .string({ message: '携帯電話を入力してください' })
    .min(1, '携帯電話を入力してください')
    .max(20, '携帯電話は20文字以内で入力してください')
    .regex(/^[0-9-]+$/, '携帯電話は数字とハイフンのみで入力してください'),

  email: z
    .string({ message: 'メールアドレスを入力してください' })
    .min(1, 'メールアドレスを入力してください')
    .email('有効なメールアドレスを入力してください')
    .max(100, 'メールアドレスは100文字以内で入力してください'),

  // 住所
  postal_code: z
    .string({ message: '郵便番号を入力してください' })
    .min(1, '郵便番号を入力してください')
    .max(10, '郵便番号は10文字以内で入力してください')
    .regex(/^\d{3}-?\d{4}$/, '郵便番号は「000-0000」の形式で入力してください'),

  region: z
    .string()
    .max(50, '地域は50文字以内で入力してください')
    .optional()
    .or(z.literal('')),

  prefecture: z
    .string({ message: '都道府県を入力してください' })
    .min(1, '都道府県を入力してください')
    .max(20, '都道府県は20文字以内で入力してください'),

  city: z
    .string({ message: '市区町村を入力してください' })
    .min(1, '市区町村を入力してください')
    .max(50, '市区町村は50文字以内で入力してください'),

  street_address: z
    .string({ message: '番地を入力してください' })
    .min(1, '番地を入力してください')
    .max(100, '番地は100文字以内で入力してください'),

  building: z
    .string()
    .max(100, '建物名は100文字以内で入力してください')
    .optional()
    .or(z.literal('')),

  // 希望情報
  desired_program_id: z
    .number({
      message: 'プログラムIDは数値である必要があります',
    })
    .optional()
    .nullable(),

  desired_join_date: z
    .union([
      z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '希望入会日は正しい形式で入力してください'),
      z.date(),
      z.null(),
    ])
    .refine(
      (date) => {
        if (!date) return true;
        const dateObj = date instanceof Date ? date : new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateObj >= today;
      },
      '希望入会日は今日以降の日付を指定してください'
    )
    .optional()
    .nullable(),

  desired_visit_date: z
    .union([
      z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '希望見学日は正しい形式で入力してください'),
      z.date(),
      z.null(),
    ])
    .refine(
      (date) => {
        if (!date) return true;
        const dateObj = date instanceof Date ? date : new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateObj >= today;
      },
      '希望見学日は今日以降の日付を指定してください'
    )
    .optional()
    .nullable(),

  // 備考
  notes: z.string().optional().or(z.literal('')),

  // 確認事項（公開フォーム専用）
  confirmation: z
    .boolean({
      message: '個人情報の取り扱いに同意してください',
    })
    .refine((val) => val === true, '個人情報の取り扱いに同意してください'),
});

/**
 * 公開フォーム入力型
 */
export type CustomerPreRegistrationPublicInput = z.infer<
  typeof customerPreRegistrationPublicSchema
>;

/**
 * 管理画面用スキーマ
 *
 * 管理者が事前登録を編集・ステータス変更する際のバリデーション
 */
export const customerPreRegistrationAdminSchema = customerPreRegistrationPublicSchema
  .omit({ confirmation: true })
  .extend({
    // ステータス変更（管理画面のみ）
    status: z
      .enum(['pending', 'confirmed', 'converted', 'cancelled'], {
        message: 'ステータスは pending, confirmed, converted, cancelled のいずれかを選択してください',
      })
      .optional(),
  });

/**
 * 管理画面入力型
 */
export type CustomerPreRegistrationAdminInput = z.infer<
  typeof customerPreRegistrationAdminSchema
>;

/**
 * 部分更新用スキーマ
 *
 * PATCH APIでの部分更新用（すべてのフィールドをオプショナルにする）
 */
export const customerPreRegistrationUpdateSchema = customerPreRegistrationAdminSchema.partial();

/**
 * 部分更新入力型
 */
export type CustomerPreRegistrationUpdateInput = z.infer<
  typeof customerPreRegistrationUpdateSchema
>;
